import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const PILLARS = [
  { icon: "🎯", title: "Strategic", text: "Every campaign starts with a sharp brief, audience map, and KPIs we own." },
  { icon: "🎨", title: "Creative", text: "Stories that earn attention — across films, posts, sites, and packaging." },
  { icon: "⚡", title: "Performance", text: "Data, dashboards, and decisions. We optimize ruthlessly." },
  { icon: "🤝", title: "Partner-first", text: "Senior team, transparent reporting, no babysitting required." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-24">
      {/* HERO */}
      <section className="container mx-auto px-4 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">About CFT Creations</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none mb-6">
              We <span className="text-gradient-animate">create experiences,</span> build trust, and drive results.
            </h1>
            <p className="text-lg text-[#52525B] leading-relaxed mb-6">
              CFT Creations is the creative & digital marketing division of Victinet IT
              Solutions Pvt. Ltd. We help startups and established brands grow through
              innovative branding, strategic marketing, and impactful digital presence.
            </p>
            <p className="text-base text-[#71717A] leading-relaxed">
              From branding and advertising to content production, studio rentals and
              business growth — everything is delivered under one roof, by a team that
              treats your brand like its own.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src="https://images.unsplash.com/photo-1758873268663-5a362616b5a7?w=900" alt="Team" className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl" />
            <div className="grid gap-3">
              <img src="https://images.unsplash.com/photo-1758873269317-51888e824b28?w=900" alt="Collaboration" className="aspect-square object-cover rounded-2xl shadow-xl mt-10" />
              <img src="https://images.unsplash.com/photo-1750905014980-c0e6ab6f3fbb?w=900" alt="Studio" className="aspect-square object-cover rounded-2xl shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="bg-zinc-50 py-24">
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ y: -6, boxShadow: "0 16px 40px rgba(255,0,127,0.12)" }}
            className="bg-white p-10 rounded-2xl border border-zinc-200 cursor-default">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Our Vision</div>
            <h3 className="font-display font-extrabold text-3xl mb-4">Transform businesses through innovation & storytelling.</h3>
            <p className="text-[#52525B] leading-relaxed">
              To become a leading creative and digital marketing brand that transforms
              businesses through innovation, storytelling, and strategic digital growth.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            whileHover={{ y: -6, boxShadow: "0 16px 40px rgba(0,191,224,0.18)" }}
            className="bg-[#0A1128] text-white p-10 rounded-2xl cursor-default">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#00BFE0] mb-3">Our Mission</div>
            <h3 className="font-display font-extrabold text-3xl mb-4">End-to-end branding & growth, under one roof.</h3>
            <ul className="space-y-2 text-white/80 text-sm leading-relaxed">
              <li>• Help businesses build strong digital identities</li>
              <li>• Create impactful, result-oriented marketing campaigns</li>
              <li>• Deliver creative solutions with modern technology</li>
              <li>• Reach the right audience effectively</li>
              <li>• Provide complete branding & marketing — under one platform</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 container mx-auto px-4">
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-12 max-w-2xl">
          Why clients <span className="text-gradient-animate">trust us</span>.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
          {PILLARS.map((p) => (
            <div key={p.title} className="bg-white p-8">
              <div className="text-3xl mb-3" aria-hidden>{p.icon}</div>
              <h4 className="font-display font-bold text-xl mb-2">{p.title}</h4>
              <p className="text-sm text-zinc-600 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-24 bg-zinc-50">
        <div className="container mx-auto px-4 grid lg:grid-cols-3 gap-10 items-start">
          <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=900"
            alt="Founder" className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl" />
          <div className="lg:col-span-2">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Founder's note</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-6">
              "Every brand has a story worth telling."
            </h2>
            <p className="text-lg text-[#52525B] leading-relaxed mb-4">
              We started CFT Creations with one belief — great brands aren't built by
              guesswork. They're built by senior craftsmen and a relentless attention to
              what actually moves the needle.
            </p>
            <p className="text-lg text-[#52525B] leading-relaxed mb-4">
              Today, with the backing of Victinet IT Solutions, we bring engineering rigor
              to creative work — from a 1500 sqft studio in Chennai to ad campaigns that
              hit eight-figure reach.
            </p>
            <p className="font-display font-bold text-xl mt-6">— The CFT Team</p>
            <div className="mt-8">
              <Link to="/contact" className="inline-flex px-6 py-3 rounded-full bg-[#0A1128] text-white font-semibold hover:bg-gradient-brand transition">
                Talk to the team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
